import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

/**
 * Owner emergency recovery landing page.
 * URL: /admin/recover?token=xxx
 * Consumes the magic-link token, mints a JWT + sets the httpOnly cookie,
 * then bounces to /admin.
 */
export default function AdminRecoverPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { setUser, refresh } = useAuth();
  const [state, setState] = useState("verifying"); // 'verifying' | 'ok' | 'error'
  const [msg, setMsg] = useState("");
  const token = params.get("token") || "";

  useEffect(() => {
    if (!token) { setState("error"); setMsg("Missing recovery token."); return; }
    (async () => {
      try {
        const r = await api.post("/auth/owner-recovery/verify", { token }).then((x) => x.data);
        if (r?.ok && r?.token) {
          setAdminToken(r.token);
          setUser(r.user);
          await refresh();
          setState("ok");
          setTimeout(() => nav("/admin", { replace: true }), 800);
        } else {
          setState("error");
          setMsg("Recovery link could not be verified.");
        }
      } catch (e) {
        setState("error");
        setMsg(e?.response?.data?.detail || e?.message || "Recovery link invalid or expired.");
      }
    })();
  }, [token]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={pageWrap} data-testid="admin-recover-page">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          NEARNOW · OWNER EMERGENCY RECOVERY
        </div>
        <h1 style={h1}>BREAK-GLASS LOGIN</h1>
        {state === "verifying" && (
          <p data-testid="recover-verifying" style={{ color: "#5b5e69" }}>
            Verifying your recovery link… If this takes more than a few seconds, the token is expired or already used.
          </p>
        )}
        {state === "ok" && (
          <p data-testid="recover-ok" style={{ color: "#1A7A4A", fontWeight: 700 }}>
            You&apos;re in. Redirecting to /admin…
          </p>
        )}
        {state === "error" && (
          <>
            <p data-testid="recover-error" style={{ color: "#A32D2D", marginBottom: 16 }}>
              {msg}
            </p>
            <button onClick={() => nav("/admin/forgot-password")} style={btn}>
              REQUEST A NEW LINK
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const pageWrap = { background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 460, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 36, color: NAVY, margin: "4px 0 22px" };
const btn = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
